// Firebase Cloud Messaging (Web Push) initialization for CarnesWork.
// All config values come from REACT_APP_FIREBASE_* env variables.
// If any required variable is missing, push is gracefully disabled.

import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

export function isPushConfigured() {
  return Boolean(config.apiKey && config.projectId && config.messagingSenderId && config.appId && VAPID_KEY);
}

export function getFirebaseApp() {
  if (!isPushConfigured()) return null;
  return getApps()[0] || initializeApp(config);
}

export async function getMessagingIfSupported() {
  if (!isPushConfigured()) return null;
  try {
    const supported = await isSupported();
    if (!supported) return null;
    return getMessaging(getFirebaseApp());
  } catch (e) {
    console.warn("[push] messaging not supported:", e);
    return null;
  }
}

export async function requestFcmToken() {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return null;
  if (!("Notification" in window)) return null;

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") return null;

  // Register the service worker explicitly so it's ready when FCM calls it.
  let swRegistration = null;
  if ("serviceWorker" in navigator) {
    try {
      swRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    } catch (e) {
      console.warn("[push] SW registration failed:", e);
      return null;
    }
  }

  try {
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration || undefined,
    });
    return token || null;
  } catch (e) {
    console.warn("[push] getToken failed:", e);
    return null;
  }
}

export async function onForegroundMessage(handler) {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}

export function getPublicConfig() {
  return { ...config, vapidKey: VAPID_KEY };
}
